import React, { useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import AnimatedSection from "../components/AnimatedSection";
import LoadingScreen from "../components/LoadingScreen";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:5000";

const categories = [
  "All",
  "Hotels & Restaurants",
  "Tea Factories & Industries",
  "Home & Living",
  "Others",
];

const Gallery = () => {
  const [products, setProducts] = useState([]);
  const [projects, setProjects] = useState([]);
  const [galleryImages, setGalleryImages] = useState([]);
  const [loading, setLoading] = useState(true);

  const [active, setActive] = useState("All");
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [allImages, setAllImages] = useState([]);

  // FETCH DATA
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [prodRes, projRes, galleryRes] = await Promise.all([
          axios.get(`${API_BASE}/api/products`),
          axios.get(`${API_BASE}/api/projects`),
          axios.get(`${API_BASE}/api/gallery`),
        ]);

        setProducts(prodRes.data || []);
        setProjects(projRes.data || []);
        setGalleryImages(galleryRes.data || []);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const matchCategory = (value, selected) => {
    return (
      typeof value === "string" &&
      typeof selected === "string" &&
      value.toLowerCase().trim() === selected.toLowerCase().trim()
    );
  };

  // MERGE IMAGES (RAW)
  useEffect(() => {
    const productImages = products.flatMap((p) =>
      (p.otherImages?.length ? p.otherImages : [p.coverImage])
        .filter(Boolean)
        .map((img, idx) => ({
          id: `${p._id}-p-${idx}`,
          src: img,
          title: p.name || "Product Image",
          category: p.category,
        }))
    );

    const projectImages = projects.flatMap((proj) =>
      proj.images?.length
        ? proj.images.map((img, idx) => ({
            id: `${proj._id}-pr-${idx}`,
            src: img.url,
            title: proj.name || "Project Image",
            category: proj.category,
          }))
        : proj.coverImage
        ? [
            {
              id: `${proj._id}-cover`,
              src: proj.coverImage,
              title: proj.name || "Project Image",
              category: proj.category,
            },
          ]
        : []
    );

    const galleryMapped = galleryImages.map((img) => ({
      id: img._id,
      src: img.url,
      title: img.title || "Gallery Image",
      category: img.category || "Others",
    }));

    const merged = [...productImages, ...projectImages, ...galleryMapped];

    // ✅ REMOVE DUPLICATES BY IMAGE URL
    const uniqueMap = new Map();

    merged.forEach((img) => {
      if (!img?.src) return;

      const existing = uniqueMap.get(img.src);

      if (!existing) {
        uniqueMap.set(img.src, img);
      } else {
        // keep the one with better title (not fallback)
        if (
          existing.title === "Product Image" ||
          existing.title === "Project Image" ||
          existing.title === "Gallery Image"
        ) {
          uniqueMap.set(img.src, img);
        }
      }
    });

    setAllImages(Array.from(uniqueMap.values()));
  }, [products, projects, galleryImages]);

  // FILTERED IMAGES
  const filteredImages =
    active === "All"
      ? allImages
      : allImages.filter((img) => matchCategory(img.category, active));

  const groupedImages = useMemo(() => {
    if (active === "All") {
      return categories.slice(1).map((cat) => ({
        category: cat,
        items: filteredImages.filter((img) => matchCategory(img.category, cat)),
      }));
    }

    return [
      {
        category: active,
        items: filteredImages,
      },
    ];
  }, [active, filteredImages]);

  const indexById = useMemo(() => {
    const map = new Map();
    filteredImages.forEach((img, idx) => {
      if (img?.id) map.set(img.id, idx);
    });
    return map;
  }, [filteredImages]);

  const openLightbox = (index) => setLightboxIndex(index);

  const handleNext = useCallback(() => {
    setLightboxIndex((prev) =>
      prev === null || filteredImages.length === 0
        ? null
        : (prev + 1) % filteredImages.length
    );
  }, [filteredImages]);

  const handlePrev = useCallback(() => {
    setLightboxIndex((prev) =>
      prev === null || filteredImages.length === 0
        ? null
        : (prev - 1 + filteredImages.length) % filteredImages.length
    );
  }, [filteredImages]);

  const handleClose = () => setLightboxIndex(null);

  useEffect(() => {
    const handleKey = (e) => {
      if (lightboxIndex === null) return;

      if (e.key === "ArrowRight") handleNext();
      if (e.key === "ArrowLeft") handlePrev();
      if (e.key === "Escape") handleClose();
    };

    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [lightboxIndex, handleNext, handlePrev]);

  useEffect(() => {
    setLightboxIndex(null);
  }, [active]);

  const currentImage =
    lightboxIndex !== null &&
    filteredImages &&
    filteredImages[lightboxIndex]
      ? filteredImages[lightboxIndex]
      : null;

  const imageName = currentImage?.title?.trim()
    ? currentImage.title
    : "Untitled Image";

  if (loading) return <LoadingScreen text="Loading gallery..." />;

  return (
    <div className="overflow-x-hidden font-inter bg-slate-100">
      {/* HERO */}
      <section className="bg-gradient-hero text-primary-foreground py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-8">
          <AnimatedSection>
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold">
              Gallery
            </h1>
            <p className="mt-3 sm:mt-4 text-base sm:text-lg opacity-80 max-w-xl">
              A visual showcase of our craftsmanship.
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* FILTER */}
      <section className="pt-8 pb-4 sm:pt-10 sm:pb-6">
        <div className="max-w-7xl mx-auto px-2 sm:px-4">
          <div className="flex gap-3 mb-6 sm:mb-8 overflow-x-auto scrollbar-hide py-2">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActive(cat)}
                className={`flex-shrink-0 px-4 sm:px-5 py-2 sm:py-2.5 rounded-full text-sm font-medium transition-all duration-300 border transform ${
                  active === cat
                    ? "bg-gradient-hero text-white shadow-md scale-105"
                    : "bg-background text-primary hover:bg-gray-100 hover:shadow-sm hover:scale-105"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* IMAGES */}
      <section className="max-w-7xl mx-auto px-2 sm:px-4 pb-20">
        {groupedImages.map(({ category, items }) =>
          items.length > 0 ? (
            <div key={category} className="mb-14 sm:mb-20">
              {/* CATEGORY TITLE */}
              <AnimatedSection>
                <div className="flex flex-row items-center gap-4 mb-6 sm:mb-10 flex-nowrap">
                  <div className="w-1 h-10 bg-gradient-to-b from-green-400 to-green-600 rounded-full flex-shrink-0" />

                  <div className="min-w-0">
                    <h2 className="text-2xl md:text-3xl font-semibold text-green-800 tracking-tight whitespace-nowrap overflow-hidden text-ellipsis">
                      {category}
                    </h2>

                    <p className="text-sm text-gray-500 mt-1 whitespace-nowrap overflow-hidden text-ellipsis">
                      Gallery highlights in {category.toLowerCase()}
                    </p>
                  </div>
                </div>
              </AnimatedSection>

              {/* MASONRY */}
              <div className="columns-2 sm:columns-3 lg:columns-4 gap-2 sm:gap-4 space-y-2 sm:space-y-4">
                {items.map((img) => (
                  <div
                    key={img.id}
                    onClick={() => openLightbox(indexById.get(img.id))}
                    className="cursor-pointer break-inside-avoid group overflow-hidden rounded-xl bg-white shadow-sm"
                  >
                    <img
                      src={img.src}
                      alt={img.title || "Gallery image"}
                      loading="lazy"
                      className="w-full rounded-xl transition-transform duration-300 group-hover:scale-[1.03] group-hover:brightness-90"
                    />
                  </div>
                ))}
              </div>
            </div>
          ) : null,
        )}

        {groupedImages.every((g) => g.items.length === 0) && (
          <p className="text-center text-muted-foreground">No images found</p>
        )}
      </section>

      {/* LIGHTBOX */}
      <AnimatePresence>
        {lightboxIndex !== null && currentImage && (
          <motion.div
            className="fixed inset-0 bg-black/90 flex items-center justify-center z-[60]"
            onClick={handleClose}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <button
              className="absolute top-5 right-5 text-white"
              onClick={(e) => {
                e.stopPropagation();
                handleClose();
              }}
            >
              <X />
            </button>

            <button
              className="absolute left-5 text-white"
              onClick={(e) => {
                e.stopPropagation();
                handlePrev();
              }}
            >
              <ChevronLeft size={40} />
            </button>

            <div
              className="flex flex-col items-center"
              onClick={(e) => e.stopPropagation()}
            >
              <motion.img
                src={currentImage.src}
                alt={imageName}
                className="max-h-[80vh] rounded-lg"
              />

              <p className="mt-4 px-4 py-2 bg-white/10 backdrop-blur rounded-lg text-white text-sm md:text-base capitalize">
                {imageName}
              </p>
            </div>

            <button
              className="absolute right-5 text-white"
              onClick={(e) => {
                e.stopPropagation();
                handleNext();
              }}
            >
              <ChevronRight size={40} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Gallery;
